import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Sparkles, CornerDownLeft } from 'lucide-react';
import useDebounce from '../../hooks/useDebounce';
import useClickOutside from '../../hooks/useClickOutside';
import searchService from '../../services/searchService';
import FileTypeIcon from '../documents/FileTypeIcon';
import { useAuth } from '../../context/AuthContext';
import { P } from '../../utils/permissions';
import cn from '../../utils/cn';

/** Global search: typeahead across names, folders and OCR content, plus the AI entry point. */
export default function SearchBar({ autoFocus }) {
  const navigate = useNavigate();
  const { can, features } = useAuth();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounced = useDebounce(query, 220);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useClickOutside(wrapRef, () => setOpen(false), open);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!debounced.trim()) {
      setResults(null);
      return undefined;
    }
    setLoading(true);
    searchService
      .suggest(debounced)
      .then((r) => !cancelled && setResults(r))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  const aiEnabled = can(P.SEARCH_AI) && features?.aiSearch;
  const flat = results ? [...results.documents, ...results.folders, ...results.content] : [];

  const go = (item) => {
    setOpen(false);
    setQuery('');
    if (!item) {
      navigate(`/search?q=${encodeURIComponent(query)}`);
      return;
    }
    navigate(item.type === 'folder' ? `/documents/${item.id}` : `/documents/${item.id}/preview`);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      go(activeIndex >= 0 ? flat[activeIndex] : null);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const Item = ({ item, index }) => (
    <button
      type="button"
      className={cn('search-pop__item', activeIndex === index && 'is-active')}
      onMouseEnter={() => setActiveIndex(index)}
      onClick={() => go(item)}
    >
      <FileTypeIcon name={item.name} type={item.type} size="sm" />
      <span className="stack truncate" style={{ gap: 1, flex: 1 }}>
        <span className="truncate" style={{ fontWeight: 600 }}>
          {item.name}
        </span>
        <span className="meta truncate">{item.path || item.department?.name}</span>
      </span>
    </button>
  );

  return (
    <div className="topbar__search" ref={wrapRef}>
      <Search size={18} strokeWidth={2} />
      <input
        ref={inputRef}
        className="in in-search"
        placeholder="Search files and their contents…"
        value={query}
        autoFocus={autoFocus}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        aria-label="Search documents"
        aria-expanded={open}
        role="combobox"
        aria-controls="search-suggestions"
      />

      {open && query.trim() && (
        <div className="search-pop" id="search-suggestions" role="listbox">
          <div className="search-pop__group">
            <button type="button" className="search-pop__item" onClick={() => go(null)}>
              <span className="stat-card__icon" style={{ width: 26, height: 26 }}>
                <Search size={14} />
              </span>
              <span className="truncate" style={{ flex: 1 }}>
                Search for <strong>{query}</strong>
              </span>
              <CornerDownLeft size={13} className="subtle" />
            </button>
            {aiEnabled && (
              <button
                type="button"
                className="search-pop__item"
                onClick={() => {
                  setOpen(false);
                  navigate(`/search/ai?q=${encodeURIComponent(query)}`);
                }}
              >
                <span className="stat-card__icon" style={{ width: 26, height: 26 }}>
                  <Sparkles size={14} />
                </span>
                <span className="truncate" style={{ flex: 1 }}>
                  Ask in natural language
                </span>
              </button>
            )}
          </div>

          {loading && !results && <div className="meta" style={{ padding: '10px 12px' }}>Searching…</div>}

          {results?.documents?.length > 0 && (
            <div className="search-pop__group">
              <div className="menu__label">Documents</div>
              {results.documents.map((d, i) => (
                <Item key={d.id} item={d} index={i} />
              ))}
            </div>
          )}
          {results?.folders?.length > 0 && (
            <div className="search-pop__group">
              <div className="menu__label">Folders</div>
              {results.folders.map((f, i) => (
                <Item key={f.id} item={f} index={results.documents.length + i} />
              ))}
            </div>
          )}
          {results?.content?.length > 0 && (
            <div className="search-pop__group">
              <div className="menu__label">Found in document content</div>
              {results.content.map((d, i) => (
                <Item key={d.id} item={d} index={results.documents.length + results.folders.length + i} />
              ))}
            </div>
          )}
          {results && flat.length === 0 && !loading && (
            <div className="meta" style={{ padding: '10px 12px' }}>
              No matches. Press Enter to run a full search.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
